import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

/**
 * Blocking modal that fires when GET /api/legal/needs-acceptance returns
 * pending document(s). The user cannot dismiss it without accepting — the
 * acceptance is logged with their IP, user-agent and timestamp.
 *
 * Mounted once at the top of the authenticated app shell.
 */
const LegalAcknowledgmentModal = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : null;

  const fetchPending = useCallback(async () => {
    if (!headers) return;
    try {
      const res = await axios.get(`${API}/legal/needs-acceptance`, { headers });
      setPending(res.data?.pending || []);
    } catch (e) {
      // Silent — non-blocking
    }
  }, [token]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const accept = async () => {
    if (!pending[0]) return;
    setLoading(true);
    setError('');
    try {
      const p = pending[0];
      await axios.post(`${API}/legal/acknowledge`,
        { doc_type: p.doc_type, doc_id: p.doc_id, ack_type: 're_accept' },
        { headers }
      );
      // Pop it off the queue and re-check (in case there are others)
      const next = pending.slice(1);
      setPending(next);
      if (next.length === 0) await fetchPending();
    } catch (e) {
      setError(e.response?.data?.detail || 'Acceptance failed. Please try again.');
    }
    setLoading(false);
  };

  if (!pending.length || !headers) return null;
  const p = pending[0];

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/85 p-4"
      data-testid="legal-acknowledgment-modal"
    >
      <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl border border-orange-500/30 bg-gray-900 shadow-2xl">
        <div className="sticky top-0 bg-gray-900/95 backdrop-blur border-b border-gray-700 p-5 flex items-center gap-3">
          <ShieldCheck className="h-6 w-6 text-orange-400" />
          <div className="flex-1 min-w-0">
            <h2 className="text-white font-bold text-lg">{p.title}</h2>
            <p className="text-xs text-gray-400">
              Version {p.version} · Effective {p.effective_date ? new Date(p.effective_date).toLocaleDateString() : '—'}
            </p>
          </div>
          <span className="rounded-full bg-orange-500/20 text-orange-300 text-[11px] px-2 py-0.5 font-medium">Required</span>
        </div>

        <div className="p-5">
          {p.summary && (
            <div className="rounded-lg border border-orange-500/30 bg-orange-500/10 p-3 mb-4 text-sm text-orange-100">
              <strong>What changed:</strong> {p.summary}
            </div>
          )}
          <div
            className="prose prose-invert prose-sm max-w-none text-gray-200 [&_h2]:text-white [&_h3]:text-white [&_a]:text-orange-300"
            dangerouslySetInnerHTML={{ __html: p.content_html }}
            data-testid="legal-modal-content"
          />
          {error && <p className="mt-3 text-sm text-red-400" data-testid="legal-modal-error">{error}</p>}
        </div>

        <div className="sticky bottom-0 bg-gray-900/95 backdrop-blur border-t border-gray-700 p-4 flex flex-col sm:flex-row gap-2 justify-end">
          <button
            onClick={accept}
            disabled={loading}
            data-testid="legal-modal-accept-btn"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-orange-600 hover:bg-orange-700 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? 'Recording…' : 'I have read and accept'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LegalAcknowledgmentModal;
